import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Mail,
  PlayCircle,
  ShieldOff,
  Loader2,
} from "lucide-react";

import { api } from "@/lib/api";

const EVENT_META = {
  invitation_opened: {
    label: "Opened",
    icon: Mail,
    cls: "text-blue-700 bg-blue-50",
  },
  video_started: {
    label: "Started watching",
    icon: PlayCircle,
    cls: "text-indigo-700 bg-indigo-50",
  },
  video_completed: {
    label: "Watched to end",
    icon: CheckCircle2,
    cls: "text-emerald-700 bg-emerald-50",
  },
  expired_attempt: {
    label: "Tried after expiry",
    icon: AlertTriangle,
    cls: "text-amber-700 bg-amber-50",
  },
  revoked_attempt: {
    label: "Tried after revoke",
    icon: ShieldOff,
    cls: "text-rose-700 bg-rose-50",
  },
};

function relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (Number.isNaN(t)) return iso;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export default function ViewerActivityFeed({
  limit = 10,
  sinceHours = 72,
  dataTestidPrefix = "viewer-activity",
}) {
  const q = useQuery({
    queryKey: ["viewer-activity-feed", limit, sinceHours],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      params.set("since_hours", String(sinceHours));
      return (await api.get(`/shares/viewer-activity-feed?${params.toString()}`)).data;
    },
    refetchInterval: 30_000,
  });

  const items = q.data?.items || [];

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
      data-testid={`${dataTestidPrefix}-tile`}
    >
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Activity size={15} className="text-indigo-600" /> Viewer activity
        </h2>
        <Link
          to="/shares"
          className="text-[11px] font-medium text-indigo-700 hover:underline"
          data-testid={`${dataTestidPrefix}-view-all`}
        >
          View all shares →
        </Link>
      </div>

      {q.isLoading ? (
        <div className="flex items-center gap-2 py-4 text-xs text-slate-500">
          <Loader2 size={13} className="animate-spin" /> Loading activity…
        </div>
      ) : items.length === 0 ? (
        <div
          className="py-6 text-center text-xs text-slate-500"
          data-testid={`${dataTestidPrefix}-empty`}
        >
          No recent viewer activity in the last {sinceHours} hours.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((it) => {
            const meta = EVENT_META[it.event_type] || {
              label: it.event_type,
              icon: Activity,
              cls: "text-slate-700 bg-slate-50",
            };
            const Icon = meta.icon;
            return (
              <li
                key={it.event_id}
                className="flex items-center justify-between gap-3 py-2"
                data-testid={`${dataTestidPrefix}-event-${it.event_id}`}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className={`grid h-7 w-7 place-items-center rounded-full ${meta.cls}`}>
                    <Icon size={13} />
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-xs text-slate-800">
                      <span className="font-medium">{it.invited_email || "Unknown viewer"}</span>{" "}
                      · {meta.label}
                    </div>
                    <div className="truncate text-[11px] text-slate-500">
                      {it.workflow_name || "—"}
                      {it.run_number != null ? ` · #${it.run_number}` : ""}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3 text-[11px] text-slate-500">
                  <span title={it.at}>{relTime(it.at)}</span>
                  <Link
                    to={`/shares/${it.invitation_id}`}
                    className="font-medium text-indigo-700 hover:underline"
                    data-testid={`${dataTestidPrefix}-open-${it.event_id}`}
                  >
                    Open
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
